import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CaseStatus } from '../../../../../enums/case-status.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ConsultationsService } from '../../../../core/services/consultation/consultations.service';
import { ToastService } from '../../../../core/services/toast.service';
import { Subscription } from 'rxjs';
import { interval, Observable, of } from 'rxjs';
import { map, switchMap, takeWhile, catchError } from 'rxjs/operators';
import { GeneratePdfService } from '../../../../core/services/generate-pdf.service';
import { PreviewFinalReportModalComponent } from '../../modals/preview-final-report-modal/preview-final-report-modal.component';

@Component({
  selector: 'app-conclusion',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PreviewFinalReportModalComponent,
  ],
  templateUrl: './conclusion.component.html',
  styleUrl: './conclusion.component.scss',
})
export class ConclusionComponent {
  constructor(
    private translateService: TranslateService,
    private consultationsService: ConsultationsService,
    private router: Router,
    private toastService: ToastService,
    private generatePdfService: GeneratePdfService
  ) {}

  @Input() patientHeaderInformations: any;
  @Input() selectedConclusionText: any;
  @Input() patientRecordCaseDetail: any;
  @Input() currentUser: any;
  @Input() consultationId: any;
  @Input() patientCaseDetails: any;
  @Input() diabetesCaseInformation: any;

  caseStatus = CaseStatus;
  previewReportErrorText: string = '';
  conclusionOptions: any = [];
  private hasAppliedAutoConclusion = false;

  ngOnInit() {
    this.getConclusionOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedConclusionText'] ||
      changes['diabetesCaseInformation']
    ) {
      this.applyAutoConclusionFromHba1c();
    }
  }

  getConclusionOptions() {
    this.consultationsService.conclusionOptions().subscribe({
      next: (res) => {
        this.conclusionOptions = res;
      },
    });
  }

  updateConclusionOutput(event: Event, optionId: string): void {
    let newLine = '';
    if (this.selectedConclusionText.length > 0) {
      newLine = '\r';
    }
    const selectedId = (event.target as HTMLSelectElement).value;

    const selectedOption = this.conclusionOptions
      .flatMap((option: any) => option.conclusionOptionContents)
      .find((item: any) => item.id === selectedId);

    const selectedText = selectedOption?.conclusionText || '';

    this.selectedConclusionText =
      this.selectedConclusionText + newLine + selectedText;
  }

  async previewReport() {
    const formData = {
      patientRecordCaseId: this.consultationId,
      patientRecordId: this.patientRecordCaseDetail?.patientRecordId,
      conclusion: this.selectedConclusionText,
    };

    this.consultationsService
      .previewReport(formData, this.patientRecordCaseDetail?.patientRecordId)
      .subscribe({
        next: () => {
          this.startCheckConsultationReport();
        },
      });
  }

  startCheckConsultationReport() {
    const checkInterval = 2000;
    let subscription: Subscription;

    subscription = interval(checkInterval)
      .pipe(
        switchMap(() => this.checkConsultationReport(this.consultationId)),
        takeWhile(
          (isConsultationReportGenerated) => !isConsultationReportGenerated,
          true
        )
      )
      .subscribe({
        next: (isConsultationReportGenerated) => {
          if (isConsultationReportGenerated) {
            this.getReport();
            subscription.unsubscribe();
          } else {
            this.previewReportErrorText = this.translateService.instant(
              'theConsultationReportIsNotReadyYet'
            );
          }
        },
        error: () => {
          subscription.unsubscribe();
        },
      });
  }

  checkConsultationReport(id: string): Observable<boolean> {
    return this.consultationsService.checkConsultationReport(id).pipe(
      map((res) => {
        return res;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  sendReport() {
    const formData: any = {
      patientRecordCaseId: this.consultationId ?? '',
      patientRecordId: this.patientRecordCaseDetail?.patientRecordId ?? '',
      conclusion: this.selectedConclusionText ?? '',
      emailTemplateBody: '',
      email: this.patientCaseDetails?.email ?? '',
      pidNo: this.currentUser?.pidNo ?? '',
    };

    this.consultationsService
      .sendReport(formData, this.patientRecordCaseDetail?.patientRecordId)
      .subscribe({
        next: () => {
          this.toastService.showToast(
            this.translateService.instant('sendingReportCompletedSuccessfully'),
            'success'
          );
          this.getReport();
          if (this.consultationId) {
            // this.getConsultationDetailById(this.consultationId);
          }
          setTimeout(() => {
            this.router.navigate(['/consultation/list']);
          }, 4000);
        },
      });
  }

  getReport() {
    this.consultationsService.getReport(this.consultationId).subscribe({
      next: (res) => {
        this.generatePdfService.displayPdf(
          res?.patientRapport,
          'previewFinalReportContent'
        );
      },
    });
  }

  private applyAutoConclusionFromHba1c(): void {
    const currentConclusion = (this.selectedConclusionText ?? '').trim();
    if (currentConclusion || this.hasAppliedAutoConclusion) {
      return;
    }

    const hba1cValue = this.getHba1cValue();
    const suggestedConclusion = this.getSuggestedConclusionText(hba1cValue);

    if (!suggestedConclusion) {
      return;
    }

    this.selectedConclusionText = suggestedConclusion;
    this.hasAppliedAutoConclusion = true;
  }

  private getHba1cValue(): number | null {
    const rawValue =
      this.diabetesCaseInformation?.laboratoryInformation?.hbA1CValue;
    const parsedValue = Number(rawValue);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private getSuggestedConclusionText(hba1cValue: number | null): string {
    if (hba1cValue === null) {
      return '';
    }

    const formattedHba1cValue = this.formatHba1cValue(hba1cValue);

    if (hba1cValue < 5.7) {
      return `HbA1c değeri (${formattedHba1cValue}) normal aralıkta olup glisemik kontrol sağlıklıdır. Diyabet açısından risk saptanmamıştır, mevcut durumun korunması önerilir. Rutin takip ve sağlıklı yaşam alışkanlıklarına devam edilmelidir.`;
    }

    if (hba1cValue <= 6.4) {
      return `HbA1c değeri (${formattedHba1cValue}) prediyabet aralığında olup diyabet gelişimi açısından risk mevcuttur. Yaşam tarzı değişiklikleri (diyet, egzersiz) önerilir. Düzenli takip ile glisemik kontrolün izlenmesi gereklidir.`;
    }

    if (hba1cValue <= 6.9) {
      return `HbA1c değeri (${formattedHba1cValue}) hedef aralığın üst sınırına yakın olup glisemik kontrol sınırda değerlendirilmektedir. Mevcut tedavi ve yaşam tarzı uyumunun artırılması önerilir. Yakın takip ile kontrolün optimize edilmesi planlanmalıdır.`;
    }

    return `HbA1c değeri (${formattedHba1cValue}) hedef aralığın üzerinde olup glisemik kontrol yetersizdir. Tedavi planının gözden geçirilmesi ve gerekli düzenlemelerin yapılması önerilir. Komplikasyon riski açısından dikkatli takip gereklidir.`;
  }

  private formatHba1cValue(hba1cValue: number): string {
    return `%${hba1cValue.toFixed(1)}`;
  }
}
